import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Single-source SEO tag injector for Postmoro public + private routes.
 *
 * Rules baked in:
 *  - Canonical always uses the production origin (https://postmoro.com)
 *    regardless of preview / staging host — matches the audit rule
 *    "no preview-domain canonicals".
 *  - Public marketing pages default to index,follow.
 *  - Private / authenticated pages must pass ``noindex`` prop so
 *    login/forgot/reset/dashboard-adjacent routes are excluded from
 *    search results.
 *  - Open Graph + Twitter Card tags mirror the visible title /
 *    description; no fabricated facts (reviews, addresses, socials).
 *
 * No structured data is rendered here — the homepage JSON-LD lives in
 * public/index.html so it appears in the initial HTML that crawlers
 * fetch before any JS executes.
 */
export const SITE_ORIGIN = "https://postmoro.com";
export const SITE_NAME = "Postmoro";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/marketing/generate.png`;

export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  noindex = false,
}) {
  const canonical =
    SITE_ORIGIN + (path.startsWith("/") ? path : `/${path}`);
  const robots = noindex ? "noindex, nofollow" : "index, follow";
  // On mount, sweep out the static baseline SEO tags from index.html so
  // Helmet becomes the sole source of truth (prevents duplicate
  // description/canonical/OG meta on non-homepage routes).
  React.useEffect(() => {
    if (typeof window !== "undefined" && typeof window.__cleanupSEOFallbacks === "function") {
      // Defer one tick so helmet-async has appended its tags first.
      setTimeout(() => {
        try { window.__cleanupSEOFallbacks(); } catch (_e) {}
      }, 0);
    }
  }, [path]);
  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}
